import React, { useState } from "react";
import { X } from "lucide-react";
import { Ornament } from "../components/Ornament";

/**
 * Preview-only: renders the dual-consent screen with a static "Mum" storyteller.
 * No auth, no API — just the UI, for screenshots and design review.
 */
export default function DesignPreviewConsent() {
  const [askerAgreed, setAskerAgreed] = useState(false);
  const [tellerAgreed, setTellerAgreed] = useState(false);
  const stName = "Mum";
  const askerLabel = "Andrea";
  const selfStory = false;
  const bothDone = selfStory ? tellerAgreed : (askerAgreed && tellerAgreed);

  return (
    <div className="min-h-screen paper" data-testid="preview-consent">
      <header className="px-6 py-5 flex items-center justify-between">
        <button className="text-[#8C8278] hover:text-[#111D33] transition p-2 -ml-2" style={{ minHeight: 44, minWidth: 44 }}>
          <X size={22} />
        </button>
        <p className="label-ui text-[10px] text-[#B8973A] tracking-[0.35em]">A GENTLE AGREEMENT</p>
        <div style={{ width: 44 }} />
      </header>

      <main className="max-w-2xl mx-auto px-6 pb-16">
        <p className="label-ui text-[11px] text-[#B8973A] tracking-[0.3em] text-center mb-6">BEFORE WE BEGIN</p>
        <h1 className="text-center text-[#111D33]" style={{ fontFamily: "Cormorant Garamond, serif", fontWeight: 300, fontSize: "clamp(2rem, 6vw, 2.75rem)", lineHeight: 1.2 }}>
          Just before {stName} and {askerLabel} begin.
        </h1>

        <div className="mt-8 bg-white hairline-cream p-6 sm:p-8" style={{ borderRadius: 4 }}>
          <p className="text-[#1B2A4A] mb-4" style={{ fontFamily: "Georgia, serif", fontSize: "17px", lineHeight: 1.7 }}>
            We're about to record your conversation so it can become a chapter of your Living Book. Here's what happens next, in plain words:
          </p>
          <ul className="space-y-3 text-[#1B2A4A]" style={{ fontFamily: "Georgia, serif", fontSize: "16px", lineHeight: 1.7 }}>
            <li className="flex gap-3"><span className="text-[#B8973A]">·</span><span><b>The audio is recorded on this phone</b> and safely stored in your private Living Book account.</span></li>
            <li className="flex gap-3"><span className="text-[#B8973A]">·</span><span><b>The words are transcribed</b> — an AI service (OpenAI Whisper) turns speech into text, then gently tidies it into a story.</span></li>
            <li className="flex gap-3"><span className="text-[#B8973A]">·</span><span><b>Only {askerLabel} and the people {askerLabel} chooses to share with</b> can listen — it is never public and never sold.</span></li>
            <li className="flex gap-3"><span className="text-[#B8973A]">·</span><span><b>Removal is easy</b> — email <a href="mailto:hello@thelivingbook.io" className="text-[#B8973A] underline underline-offset-2">hello@thelivingbook.io</a> anytime and we will delete the recording and any words from it.</span></li>
          </ul>
        </div>

        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="bg-white hairline-cream p-6 flex flex-col items-center text-center" style={{ borderRadius: 4 }}>
            <p className="label-ui text-[10px] text-[#B8973A] tracking-[0.3em]">THE ASKER</p>
            <p className="mt-2 text-[#1B2A4A]" style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "1.4rem", fontStyle: "italic" }}>{askerLabel}</p>
            {askerAgreed ? (
              <div className="mt-4 flex items-center gap-2 text-[#3D5C2E]" style={{ fontFamily: "Georgia, serif", fontSize: "15px" }}>
                <span aria-hidden="true">✓</span> I agree
              </div>
            ) : (
              <button type="button" onClick={() => setAskerAgreed(true)} className="btn-gold mt-5 w-full" style={{ minHeight: 72, fontSize: "16px", letterSpacing: "0.2em", padding: "18px 24px" }}>
                I AGREE
              </button>
            )}
          </div>
          <div className="bg-white hairline-cream p-6 flex flex-col items-center text-center" style={{ borderRadius: 4 }}>
            <p className="label-ui text-[10px] text-[#B8973A] tracking-[0.3em]">THE STORYTELLER</p>
            <p className="mt-2 text-[#1B2A4A]" style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "1.4rem", fontStyle: "italic" }}>{stName}</p>
            {tellerAgreed ? (
              <div className="mt-4 flex items-center gap-2 text-[#3D5C2E]" style={{ fontFamily: "Georgia, serif", fontSize: "15px" }}>
                <span aria-hidden="true">✓</span> I agree
              </div>
            ) : (
              <button type="button" onClick={() => setTellerAgreed(true)} className="btn-gold mt-5 w-full" style={{ minHeight: 72, fontSize: "16px", letterSpacing: "0.2em", padding: "18px 24px" }}>
                I AGREE
              </button>
            )}
          </div>
        </div>

        {bothDone ? (
          <div className="mt-8 text-center">
            <p className="italic text-[#3D5C2E] mb-4" style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "1.15rem" }}>
              Thank you. When you're ready, tap below to begin.
            </p>
            <button type="button" className="btn-gold" style={{ minHeight: 60, padding: "16px 40px", fontSize: "14px", letterSpacing: "0.25em" }}>
              CONTINUE TO RECORDING &rarr;
            </button>
          </div>
        ) : (
          <p className="mt-8 text-center italic text-[#8C8278]" style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "1.05rem" }}>
            Both taps together — no rush.
          </p>
        )}
      </main>
      <footer className="py-8 text-center"><Ornament color="#B8973A" /></footer>
    </div>
  );
}
